export interface ApiResponse<T = null> {
  code: number;
  status: number;
  message: string;
  data: T | null;
}

interface ApiError extends Error {
  code: number;
  status: number;
}

const BASE_URL = '';

async function request<T, K = undefined>(
  url: string,
  options: RequestInit,
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${BASE_URL}${url}`, options);
    const contentType = response.headers.get('Content-Type');

    let responseData: ApiResponse<T> | null = null;

    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    }

    if (!response.ok || responseData?.code !== 0) {
      const error = new Error(responseData?.message || 'An error occurred') as ApiError;
      error.code = responseData?.code || -1;
      error.status = response.status;
      throw error;
    }

    return responseData as ApiResponse<T>;
  } catch (error) {
    if (error instanceof Error) {
      const apiError = error as ApiError;
      apiError.code = apiError.code || -1;
      apiError.status = apiError.status || 500;
      apiError.message = apiError.message || '';
      throw {
        code: apiError.code || -1,
        status: apiError.status || 500,
        message: error.message,
      };
    } else {
      throw {
        code: -1,
        status: 500,
        message: 'Unknown error occurred',
      };
    }
  }
}

export const api = {
  get: async function <T = null>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return request<T>(url, { method: 'GET', ...options });
  },

  post: async function <T = undefined, K = null>(
    url: string,
    body?: T | FormData,
    options?: RequestInit,
  ): Promise<ApiResponse<K>> {
    const isFormData = body instanceof FormData;

    return request<K>(url, {
      method: 'POST',
      headers: isFormData
        ? { ...options?.headers }
        : { 'Content-Type': 'application/json', ...options?.headers },
      body: isFormData ? body : JSON.stringify(body),
      ...options,
    });
  },

  delete: async function <T = null>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return request<T>(url, { method: 'DELETE', ...options });
  },
};
